import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, Observable, take } from 'rxjs';

import { AncillaryUpsellNode, PortalOrderNode, PortalTierEnum } from '@app/services/api/api.types';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { Steps } from '@app/components/stepper/stepper.component';
import {
  PopupsService,
  PriceSummaryAction,
  Tab,
  maxHideSwipeRange,
} from '@voyagu/portal-design-system';
import { getTiers, getRows } from '@app/pages/tiers-page/utils';
import { StateService } from '@app/services/state/state.service';
import { PortalRoutes } from '@app/app-routing.module';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { PortalCurrencyPipe } from '@app/pipes/portal-currency.pipe';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalTierCatalogService } from '@app/services/portal-tier-catalog/portal-tier-catalog.service';
import { containerWidth } from '@app/utils/constants';
import { AuthService } from '@app/services/auth/auth.service';
import { handleError } from '@app/operators/handle-error';
import { ErrorService } from '@app/services/error.service';
import {
  checkoutExtrasFromState,
  SessionProceedCoordinator,
} from '@app/services/session-proceed-coordinator.service';
import { CHECKOUT_BASIC_TIER_QUERY_VALUE } from '@app/utils/checkout-resume.util';
import { getNextRouteAfterTiers } from '@app/pages/ancillary-upsell-page/ancillary-upsell-navigation.util';
import { isEmbeddedInIframe } from '@app/utils/utils';
import { MemoizedValue } from '@app/utils/memoized-value.util';

@Component({
  selector: 'tiers-page',
  templateUrl: './tiers-page.component.html',
  styleUrl: './tiers-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SessionProceedCoordinator],
})
export class TiersPageComponent implements OnInit {
  isEmbedded = isEmbeddedInIframe();

  Steps = Steps;
  PortalTierEnum = PortalTierEnum;

  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  location = inject(Location);
  stateService = inject(StateService);
  portalOrderService = inject(PortalOrderService);
  portalLeadService = inject(PortalLeadService);
  portalTierCatalogService = inject(PortalTierCatalogService);
  portalCurrencyPipe = inject(PortalCurrencyPipe);
  popupsService = inject(PopupsService);
  authService = inject(AuthService);
  errorService = inject(ErrorService);
  sessionProceed = inject(SessionProceedCoordinator);
  changeDetectorRef = inject(ChangeDetectorRef);

  portalOrder$: Observable<PortalOrderNode | null>;
  portalOrder: PortalOrderNode | null;

  tiers: { [key: string]: any }[] = [];
  selectedTier: { [key: string]: any };

  tierTabs: Tab[] = [];
  selectedTierTab: Tab;

  sectionTabs: Tab[] = [
    { label: 'Options Summary', value: true, icon: 'summary' },
    { label: 'Compare Options', value: false, icon: 'compare' },
  ];
  selectedSectionTab = this.sectionTabs[0];

  rows: {
    label: string;
    key: string;
    items: {
      label: string;
      key: string;
      tooltip?: string;
    }[];
  }[] = [];

  hoveredRowItem: unknown;
  isNewUser: boolean;
  ancillaryUpsell: boolean;

  startX = 0;

  @ViewChild('upsellOptionModalTemplate') upsellOptionModalTemplate: TemplateRef<unknown>;
  @ViewChild('askToSignInTemplate') askToSignInTemplate: TemplateRef<unknown>;

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
      this.portalTierCatalogService.catalog$,
    ]).pipe(
      map(([portalOrder, portalLead, catalog]) => {
        if (portalOrder) {
          this.portalOrder = portalOrder;

          const upsellOptions = this.ancillaryUpsell
            ? (portalOrder as unknown as AncillaryUpsellNode).availableUpsellOptions
            : null;

          this.isNewUser = !!portalLead?.isNewUser;

          this.rows = getRows(portalLead);

          this.tiers = getTiers(
            catalog,
            portalOrder,
            this.portalCurrencyPipe.transform,
            portalLead?.salesAgent?.agencyName ?? '',
            upsellOptions,
          );

          this.tierTabs = this.tiers.map(
            (t) => ({ value: t.value, label: t.label, disabled: !!t.disabled }) as Tab,
          );

          this.selectTierTab(this.getDefaultTierTab());
        }

        return portalOrder;
      }),
    );

    Tracking.track(PortalTrackingEvents.tiersPageOpened);
  }

  getDefaultTierTab(): Tab {
    const state = this.stateService.getState();
    const stateTier = state.tier;
    const enabledTiers = this.tiers.filter((tier) => !tier.disabled);

    if (!stateTier && state.basicTierSelected) {
      const basicTier = enabledTiers.find((tier) => !tier.value);
      const basicIndex = basicTier ? this.tiers.indexOf(basicTier) : -1;

      if (basicIndex >= 0) {
        return this.tierTabs[basicIndex] ?? this.tierTabs[0];
      }
    }

    const defaultTier =
      (stateTier && enabledTiers.find((tier) => tier.value === stateTier)) ||
      enabledTiers.find((tier) => tier.value === PortalTierEnum.VIP) ||
      enabledTiers.at(-1) ||
      this.tiers[0];

    const tierIndex = this.tiers.indexOf(defaultTier);

    return this.tierTabs[tierIndex] ?? this.tierTabs[0];
  }

  selectTierTab(selectedTierTab: Tab) {
    if (selectedTierTab.disabled) {
      return;
    }

    const tier = this.tiers[this.tierTabs.findIndex((t) => t === selectedTierTab)];

    this.selectedTierTab = selectedTierTab;
    this.selectTier(tier);
  }

  get essentialUpsellPricePerPassenger() {
    const basicTier = this.tiers.find((tier) => !tier.value);
    const essentialTier = this.tiers.find((tier) => tier.value === PortalTierEnum.ESSENTIAL);

    if (!basicTier || !essentialTier || !this.portalOrder) {
      return 0;
    }

    return essentialTier.soldAmountPerAdult - basicTier.soldAmountPerAdult;
  }

  selectTier(selectedTier: { [key: string]: any }) {
    if (selectedTier.disabled) {
      return;
    }

    this.selectedTier = selectedTier;

    Tracking.track(PortalTrackingEvents.tiersPageSelectTier, {
      tier: (selectedTier.value as string) || 'BASIC',
    });

    this.stateService.patchState({
      tier: this.selectedTier.value as PortalTierEnum,
      basicTierSelected: !this.selectedTier.value,
    });
  }

  back() {
    Tracking.track(PortalTrackingEvents.tiersPageBackPressed);

    this.location.back();
  }

  continue() {
    Tracking.track(PortalTrackingEvents.tiersPageSubmitPressed);

    if (!this.selectedTier?.value && !this.ancillaryUpsell) {
      this.popupsService.showModal(this.upsellOptionModalTemplate);

      return;
    }

    this.checkSession();
  }

  checkSession() {
    this.authService
      .hasSession()
      .pipe(
        take(1),
        map((hasSession) => {
          if (hasSession || this.isNewUser) {
            return this.redirect();
          }
          this.showAskToSignInModal();
        }),
        handleError(this.errorService),
      )
      .subscribe();
  }

  showAskToSignInModal() {
    this.popupsService.showModal(this.askToSignInTemplate);
    this.sessionProceed.trackSignInFromCheckoutPromptOpened();
  }

  showSignInModal() {
    const fromState = checkoutExtrasFromState(this.stateService.getState()) ?? {};

    this.sessionProceed.openSignInModalForPortalOption(
      this.stateService.getState().portalLink,
      this.portalOrder?.portalLink ?? '',
      {
        ...fromState,
        tier:
          this.selectedTier?.value != null
            ? String(this.selectedTier.value)
            : CHECKOUT_BASIC_TIER_QUERY_VALUE,
        checkoutResume: PortalRoutes.Travelers,
        checkoutPath: 'option',
      },
    );
  }

  redirect() {
    let path: (string | null | undefined)[];

    if (this.ancillaryUpsell) {
      path = [
        PortalRoutes.AncillaryUpsell,
        this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam),
        getNextRouteAfterTiers(),
      ];
    } else {
      path = [
        `${this.stateService.getState().portalLink}`,
        `${this.portalOrder?.portalLink}`,
        PortalRoutes.Travelers,
      ];
    }

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  hideModal() {
    this.popupsService.hideLast();
  }

  upgrade() {
    Tracking.track(PortalTrackingEvents.tiersPageUpgradePressed);

    this.selectTier(this.tiers[1]);

    this.hideModal();

    this.checkSession();
  }

  review() {
    this.selectTier(this.tiers[1]);
    this.selectTierTab(this.tierTabs[1]);

    if (!isMobile()) {
      this.selectedSectionTab = this.sectionTabs[1];
    }

    this.changeDetectorRef.detectChanges();

    this.hideModal();
  }

  pointerDown(event: PointerEvent) {
    if (!isMobile()) return;

    this.startX = event.clientX;
  }

  pointerUp(event: PointerEvent) {
    if (!isMobile()) return;

    const endX = event.clientX;
    const diff = this.startX - endX;

    if (Math.abs(diff) > maxHideSwipeRange) {
      if (diff > 0) {
        this.onSwipeLeft();
      } else {
        this.onSwipeRight();
      }
    }
  }

  onSwipeLeft() {
    const foundIndex = this.tierTabs.findIndex((tierTab) => tierTab === this.selectedTierTab);
    const nextTierTab = this.tierTabs[foundIndex + 1];

    if (nextTierTab && !nextTierTab.disabled) {
      this.selectTierTab(nextTierTab);
    }
  }

  onSwipeRight() {
    const foundIndex = this.tierTabs.findIndex((tierTab) => tierTab === this.selectedTierTab);
    const prevTierTab = this.tierTabs[foundIndex - 1];

    if (prevTierTab && !prevTierTab.disabled) {
      this.selectTierTab(prevTierTab);
    }
  }

  summaryActionsCache = new MemoizedValue<PriceSummaryAction[]>();

  get summaryActions(): PriceSummaryAction[] {
    const continueLabel = this.summaryContinueLabel();

    return this.summaryActionsCache.get(String(continueLabel), () => {
      const actions: PriceSummaryAction[] = [];

      if (continueLabel) {
        actions.push({ id: 'continue', label: continueLabel, variant: 'primary' });
      }

      actions.push({ id: 'back', label: 'Back', variant: 'secondary' });

      return actions;
    });
  }

  summaryContinueLabel(): string | null {
    const tier = this.selectedTier;
    const portalOrder = this.portalOrder;

    if (!tier || !portalOrder || tier.disabled || this.isEmbedded) {
      return null;
    }

    if (this.ancillaryUpsell && tier.isYourPlan) {
      return 'Continue';
    }

    if (tier.isYourPlan || tier.includedInEssential) {
      return null;
    }

    const tierAmount = Number(tier.soldAmountPerAdult);
    const soldAmountPerAdult = this.ancillaryUpsell
      ? tierAmount
      : tierAmount + portalOrder.soldAmountPerAdult;
    const continueLabel = this.ancillaryUpsell ? 'Upgrade' : 'Continue';

    return `${continueLabel} for ${this.portalCurrencyPipe.transform(soldAmountPerAdult)} pp`;
  }

  onSummaryAction(action: PriceSummaryAction) {
    if (action.id === 'back') {
      this.back();
      return;
    }

    this.continue();
  }
}

const isMobile = () => {
  return window.innerWidth < containerWidth;
};
